// 앱 라우팅 — 페이지는 React.lazy 로 동적 로딩 (초기 번들 최소화)
import { lazy, Suspense } from 'react'
import { Routes, Route } from 'react-router-dom'
import ProtectedRoute from '@/components/auth/ProtectedRoute'
import PublicRoute from '@/components/auth/PublicRoute'

// ── 인증/공통 페이지 ───────────────────────────────────────
const LoginPage = lazy(() => import('@/pages/LoginPage'))
const OnboardingPage = lazy(() => import('@/pages/OnboardingPage'))
const UnauthorizedPage = lazy(() => import('@/pages/UnauthorizedPage'))

// ── Admin 페이지 ───────────────────────────────────────────
const DashboardPage = lazy(() => import('@/pages/DashboardPage'))
const CalendarPage = lazy(() => import('@/pages/CalendarPage'))

const ExhibitionsPage = lazy(() => import('@/pages/ExhibitionsPage'))
const ExhibitionFormPage = lazy(() => import('@/pages/ExhibitionFormPage'))
const ExhibitionDetailPage = lazy(() => import('@/pages/ExhibitionDetailPage'))

const ArtworksPage = lazy(() => import('@/pages/ArtworksPage'))
const ArtworkFormPage = lazy(() => import('@/pages/ArtworkFormPage'))
const ArtworkDetailPage = lazy(() => import('@/pages/ArtworkDetailPage'))

const ArtistsPage = lazy(() => import('@/pages/ArtistsPage'))
const ArtistFormPage = lazy(() => import('@/pages/ArtistFormPage'))
const ArtistDetailPage = lazy(() => import('@/pages/ArtistDetailPage'))

const ContractsPage = lazy(() => import('@/pages/ContractsPage'))
const ContractFormPage = lazy(() => import('@/pages/ContractFormPage'))
const ContractDetailPage = lazy(() => import('@/pages/ContractDetailPage'))

const QuotesPage = lazy(() => import('@/pages/QuotesPage'))
const QuoteFormPage = lazy(() => import('@/pages/QuoteFormPage'))

const SaleConfirmationsPage = lazy(() => import('@/pages/SaleConfirmationsPage'))
const SaleConfirmationFormPage = lazy(() => import('@/pages/SaleConfirmationFormPage'))

const ArtistPrepEmailsPage = lazy(() => import('@/pages/ArtistPrepEmailsPage'))

const CustomersPage = lazy(() => import('@/pages/CustomersPage'))
const CustomerFormPage = lazy(() => import('@/pages/CustomerFormPage'))
const CustomerDetailPage = lazy(() => import('@/pages/CustomerDetailPage'))

const GroupsPage = lazy(() => import('@/pages/GroupsPage'))
const GroupFormPage = lazy(() => import('@/pages/GroupFormPage'))
const GroupDetailPage = lazy(() => import('@/pages/GroupDetailPage'))

const CatalogsPage = lazy(() => import('@/pages/CatalogsPage'))
const CatalogFormPage = lazy(() => import('@/pages/CatalogFormPage'))

const SettlementsPage = lazy(() => import('@/pages/SettlementsPage'))
const LedgerPage = lazy(() => import('@/pages/LedgerPage'))
const ReportsPage = lazy(() => import('@/pages/ReportsPage'))
const SettingsPage = lazy(() => import('@/pages/SettingsPage'))

// ── 결제 ───────────────────────────────────────────────────
const PaymentSuccessPage = lazy(() => import('@/pages/PaymentSuccessPage'))
const PaymentFailPage = lazy(() => import('@/pages/PaymentFailPage'))
const ArtworkPaymentPage = lazy(() => import('@/pages/ArtworkPaymentPage'))
const ArtworkPaymentSuccessPage = lazy(() => import('@/pages/ArtworkPaymentSuccessPage'))
const ArtworkPaymentFailPage = lazy(() => import('@/pages/ArtworkPaymentFailPage'))

// ── Public 페이지 ──────────────────────────────────────────
const LandingPage = lazy(() => import('@/pages/public/LandingPage'))
const LandingPageEN = lazy(() => import('@/pages/public/LandingPageEN'))
const LandingPageJA = lazy(() => import('@/pages/public/LandingPageJA'))
const PricingPage = lazy(() => import('@/pages/public/PricingPage'))
const AboutPage = lazy(() => import('@/pages/public/AboutPage'))
const RegisterPage = lazy(() => import('@/pages/public/RegisterPage'))
const ResetPasswordPage = lazy(() => import('@/pages/public/ResetPasswordPage'))
const GalleryPublicPage = lazy(() => import('@/pages/public/GalleryPublicPage'))
const ArtistPublicPage = lazy(() => import('@/pages/public/ArtistPublicPage'))
const GalleryMapPage = lazy(() => import('@/pages/public/GalleryMapPage'))
const GuestbookPage = lazy(() => import('@/pages/public/GuestbookPage'))
const UnsubscribePage = lazy(() => import('@/pages/public/UnsubscribePage'))
const GalleryDirectoryPage = lazy(() => import('@/pages/public/GalleryDirectoryPage'))
const ArtworkShopPage = lazy(() => import('@/pages/public/ArtworkShopPage'))
const StoriesPage = lazy(() => import('@/pages/public/StoriesPage'))
const BlogPostPage = lazy(() => import('@/pages/public/BlogPostPage'))
const SeminarDetailPage = lazy(() => import('@/pages/public/SeminarDetailPage'))

// ── Super Admin ────────────────────────────────────────────
const SADashboardPage = lazy(() => import('@/pages/super-admin/SADashboardPage'))
const SAGalleriesPage = lazy(() => import('@/pages/super-admin/SAGalleriesPage'))
const SAGalleryDetailPage = lazy(() => import('@/pages/super-admin/SAGalleryDetailPage'))
const SASubscriptionsPage = lazy(() => import('@/pages/super-admin/SASubscriptionsPage'))
const SATransactionsPage = lazy(() => import('@/pages/super-admin/SATransactionsPage'))
const SAUsersPage = lazy(() => import('@/pages/super-admin/SAUsersPage'))
const SAContentPage = lazy(() => import('@/pages/super-admin/SAContentPage'))
const SAVenuesPage = lazy(() => import('@/pages/super-admin/SAVenuesPage'))
const SASupportPage = lazy(() => import('@/pages/super-admin/SASupportPage'))
const SAAnnouncementsPage = lazy(() => import('@/pages/super-admin/SAAnnouncementsPage'))
const SAReportsPage = lazy(() => import('@/pages/super-admin/SAReportsPage'))
const SAPipelinePage = lazy(() => import('@/pages/super-admin/SAPipelinePage'))
const SASettingsPage = lazy(() => import('@/pages/super-admin/SASettingsPage'))
const SANewsPage = lazy(() => import('@/pages/super-admin/SANewsPage'))

// ── Suspense fallback — Canvly 브랜드 로딩 화면 ─────────────
function PageFallback() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: '#FDFCFA',
      }}
    >
      <div
        style={{
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontSize: '32px',
          color: '#C4973A',
          letterSpacing: '10px',
          fontWeight: 500,
        }}
      >
        CANVLY
      </div>
    </div>
  )
}

export default function AppRoutes() {
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        {/* 갤러리 공개 페이지 */}
        <Route path="/g/:slug" element={<GalleryPublicPage />} />
        <Route path="/artist/:artistId" element={<ArtistPublicPage />} />
        <Route path="/map" element={<GalleryMapPage />} />
        <Route path="/visit/:exhibitionId" element={<GuestbookPage />} />
        <Route path="/unsubscribed" element={<UnsubscribePage kind="success" />} />
        <Route path="/unsubscribe-error" element={<UnsubscribePage kind="error" />} />

        {/* 작품 결제 (공개) */}
        <Route path="/artwork-payment" element={<ArtworkPaymentPage />} />
        <Route path="/artwork-payment/success" element={<ArtworkPaymentSuccessPage />} />
        <Route path="/artwork-payment/fail" element={<ArtworkPaymentFailPage />} />

        {/* Canvly 홈페이지 (공개) — 다국어 */}
        <Route path="/home" element={<LandingPage />} />
        <Route path="/en" element={<LandingPageEN />} />
        <Route path="/ja" element={<LandingPageJA />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* 아트 플랫폼 공개 섹션 */}
        <Route path="/galleries" element={<GalleryDirectoryPage />} />
        <Route path="/shop" element={<ArtworkShopPage />} />
        <Route path="/stories" element={<StoriesPage />} />
        <Route path="/stories/blog/:slug" element={<BlogPostPage />} />
        <Route path="/events/:slug" element={<SeminarDetailPage />} />

        {/* 공개 라우트 */}
        <Route path="/login" element={
          <PublicRoute><LoginPage /></PublicRoute>
        } />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/unauthorized" element={<UnauthorizedPage />} />

        {/* 보호된 라우트 */}
        <Route path="/dashboard" element={
          <ProtectedRoute><DashboardPage /></ProtectedRoute>
        } />

        {/* 전시 캘린더 */}
        <Route path="/calendar" element={
          <ProtectedRoute><CalendarPage /></ProtectedRoute>
        } />

        {/* 전시 */}
        <Route path="/exhibitions" element={
          <ProtectedRoute><ExhibitionsPage /></ProtectedRoute>
        } />
        <Route path="/exhibitions/new" element={
          <ProtectedRoute><ExhibitionFormPage /></ProtectedRoute>
        } />
        <Route path="/exhibitions/:id" element={
          <ProtectedRoute><ExhibitionDetailPage /></ProtectedRoute>
        } />
        <Route path="/exhibitions/:id/edit" element={
          <ProtectedRoute><ExhibitionFormPage /></ProtectedRoute>
        } />

        {/* 작품 */}
        <Route path="/artworks" element={
          <ProtectedRoute><ArtworksPage /></ProtectedRoute>
        } />
        <Route path="/artworks/new" element={
          <ProtectedRoute><ArtworkFormPage /></ProtectedRoute>
        } />
        <Route path="/artworks/:id/edit" element={
          <ProtectedRoute><ArtworkFormPage /></ProtectedRoute>
        } />
        <Route path="/artworks/:id" element={
          <ProtectedRoute><ArtworkDetailPage /></ProtectedRoute>
        } />

        {/* 작가 */}
        <Route path="/artists" element={
          <ProtectedRoute><ArtistsPage /></ProtectedRoute>
        } />
        <Route path="/artists/:id" element={
          <ProtectedRoute><ArtistDetailPage /></ProtectedRoute>
        } />

        {/* 단체 (DB 테이블/URL은 groups 유지) */}
        <Route path="/groups" element={
          <ProtectedRoute><GroupsPage /></ProtectedRoute>
        } />
        <Route path="/groups/:id" element={
          <ProtectedRoute><GroupDetailPage /></ProtectedRoute>
        } />
        <Route path="/groups/new" element={
          <ProtectedRoute><GroupFormPage /></ProtectedRoute>
        } />
        <Route path="/groups/:id/edit" element={
          <ProtectedRoute><GroupFormPage /></ProtectedRoute>
        } />
        <Route path="/artists/new" element={
          <ProtectedRoute><ArtistFormPage /></ProtectedRoute>
        } />
        <Route path="/artists/:id/edit" element={
          <ProtectedRoute><ArtistFormPage /></ProtectedRoute>
        } />

        {/* 계약 */}
        <Route path="/contracts" element={
          <ProtectedRoute><ContractsPage /></ProtectedRoute>
        } />
        <Route path="/contracts/new" element={
          <ProtectedRoute><ContractFormPage /></ProtectedRoute>
        } />
        <Route path="/contracts/:id" element={
          <ProtectedRoute><ContractDetailPage /></ProtectedRoute>
        } />
        <Route path="/contracts/:id/edit" element={
          <ProtectedRoute><ContractFormPage /></ProtectedRoute>
        } />

        {/* 견적 */}
        <Route path="/quotes" element={
          <ProtectedRoute><QuotesPage /></ProtectedRoute>
        } />
        <Route path="/quotes/new" element={
          <ProtectedRoute><QuoteFormPage /></ProtectedRoute>
        } />
        <Route path="/quotes/:id" element={
          <ProtectedRoute><QuoteFormPage /></ProtectedRoute>
        } />

        {/* 판매확인서·보증서 */}
        <Route path="/sale-confirmations" element={
          <ProtectedRoute><SaleConfirmationsPage /></ProtectedRoute>
        } />
        <Route path="/sale-confirmations/new" element={
          <ProtectedRoute><SaleConfirmationFormPage /></ProtectedRoute>
        } />
        <Route path="/sale-confirmations/:id" element={
          <ProtectedRoute><SaleConfirmationFormPage /></ProtectedRoute>
        } />

        {/* 작가 사전 안내메일 */}
        <Route path="/artist-prep-emails" element={
          <ProtectedRoute><ArtistPrepEmailsPage /></ProtectedRoute>
        } />

        {/* 고객 */}
        <Route path="/customers" element={
          <ProtectedRoute><CustomersPage /></ProtectedRoute>
        } />
        <Route path="/customers/new" element={
          <ProtectedRoute><CustomerFormPage /></ProtectedRoute>
        } />
        <Route path="/customers/:id" element={
          <ProtectedRoute><CustomerDetailPage /></ProtectedRoute>
        } />
        <Route path="/customers/:id/edit" element={
          <ProtectedRoute><CustomerFormPage /></ProtectedRoute>
        } />

        {/* 도록 */}
        <Route path="/catalogs" element={
          <ProtectedRoute><CatalogsPage /></ProtectedRoute>
        } />
        <Route path="/catalogs/new" element={
          <ProtectedRoute><CatalogFormPage /></ProtectedRoute>
        } />
        <Route path="/catalogs/:id/edit" element={
          <ProtectedRoute><CatalogFormPage /></ProtectedRoute>
        } />

        {/* 장부 */}
        <Route path="/ledger" element={
          <ProtectedRoute><LedgerPage /></ProtectedRoute>
        } />

        {/* 보고서 */}
        <Route path="/reports" element={
          <ProtectedRoute><ReportsPage /></ProtectedRoute>
        } />

        {/* 정산 */}
        <Route path="/settlements" element={
          <ProtectedRoute><SettlementsPage /></ProtectedRoute>
        } />

        {/* 설정 */}
        <Route path="/settings" element={
          <ProtectedRoute><SettingsPage /></ProtectedRoute>
        } />

        {/* 결제 */}
        <Route path="/payment/success" element={
          <ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>
        } />
        <Route path="/payment/fail" element={
          <ProtectedRoute><PaymentFailPage /></ProtectedRoute>
        } />

        {/* 슈퍼 어드민 */}
        <Route path="/super-admin" element={<ProtectedRoute><SADashboardPage /></ProtectedRoute>} />
        <Route path="/super-admin/galleries" element={<ProtectedRoute><SAGalleriesPage /></ProtectedRoute>} />
        <Route path="/super-admin/galleries/:id" element={<ProtectedRoute><SAGalleryDetailPage /></ProtectedRoute>} />
        <Route path="/super-admin/pipeline" element={<ProtectedRoute><SAPipelinePage /></ProtectedRoute>} />
        <Route path="/super-admin/subscriptions" element={<ProtectedRoute><SASubscriptionsPage /></ProtectedRoute>} />
        <Route path="/super-admin/transactions" element={<ProtectedRoute><SATransactionsPage /></ProtectedRoute>} />
        <Route path="/super-admin/users" element={<ProtectedRoute><SAUsersPage /></ProtectedRoute>} />
        <Route path="/super-admin/content" element={<ProtectedRoute><SAContentPage /></ProtectedRoute>} />
        <Route path="/super-admin/venues" element={<ProtectedRoute><SAVenuesPage /></ProtectedRoute>} />
        <Route path="/super-admin/news" element={<ProtectedRoute><SANewsPage /></ProtectedRoute>} />
        <Route path="/super-admin/support" element={<ProtectedRoute><SASupportPage /></ProtectedRoute>} />
        <Route path="/super-admin/announcements" element={<ProtectedRoute><SAAnnouncementsPage /></ProtectedRoute>} />
        <Route path="/super-admin/reports" element={<ProtectedRoute><SAReportsPage /></ProtectedRoute>} />
        <Route path="/super-admin/settings" element={<ProtectedRoute><SASettingsPage /></ProtectedRoute>} />

        {/* 기본 */}
        <Route path="/" element={<LandingPage />} />
        <Route path="*" element={<LandingPage />} />
      </Routes>
    </Suspense>
  )
}
